<template>
  <div>
    <Nav></Nav>
    <b-container fluid="xl">
      <b-row class="mt-3">
        <b-col>
          <b-button variant="link" @click="$router.push('/customers')">&larr; Clientes</b-button>
          <h1>{{ customer.name || 'Cliente #' + customer.id }}</h1>
          <p class="text-muted">
            uuid: <code>{{ customer.uuid }}</code> · código de referido: <code>{{ customer.referral_code }}</code>
          </p>
        </b-col>
      </b-row>

      <b-row v-if="loading"><b-col class="text-center my-4"><b-spinner></b-spinner></b-col></b-row>

      <b-tabs v-else content-class="mt-3">
        <b-tab title="Perfil" active>
          <b-row>
            <b-col md="6">
              <b-form-group label="Nombre"><b-form-input v-model="customer.name"></b-form-input></b-form-group>
              <b-form-group label="Email"><b-form-input v-model="customer.email"></b-form-input></b-form-group>
              <b-form-group label="Teléfono"><b-form-input v-model="customer.phone"></b-form-input></b-form-group>
              <b-form-group label="Cumpleaños (YYYY-MM-DD)"><b-form-input v-model="customer.birthdate"></b-form-input></b-form-group>
              <b-form-group label="Sexo"><b-form-input v-model="customer.gender"></b-form-input></b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Código postal"><b-form-input v-model="customer.postal_code"></b-form-input></b-form-group>
              <b-form-group label="Zona / colonia"><b-form-input v-model="customer.zone"></b-form-input></b-form-group>
              <b-form-group label="Sucursal preferida (id)"><b-form-input type="number" v-model.number="preferredSucursal"></b-form-input></b-form-group>
              <b-form-group label="Estado">
                <b-form-select v-model="customer.status" :options="statusOptions"></b-form-select>
              </b-form-group>
              <b-form-checkbox v-model="customer.marketing_opt_in" switch>Acepta marketing</b-form-checkbox>
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col>
              <b-button variant="primary" @click="save">Guardar perfil</b-button>
            </b-col>
          </b-row>

          <b-row class="mt-4">
            <b-col>
              <h5>Identidades vinculadas</h5>
              <b-table small striped :items="customer.identities || []" :fields="identityFields">
                <template #empty>Sin identidades vinculadas.</template>
              </b-table>
            </b-col>
          </b-row>
        </b-tab>

        <b-tab title="Lealtad" @click="loadLoyalty">
          <div v-if="loyalty.account">
            <b-row>
              <b-col md="3"><b-card><h6>Saldo</h6><h3>{{ loyalty.account.points_balance }}</h3><small class="text-muted">pendiente: {{ loyalty.account.points_pending }}</small></b-card></b-col>
              <b-col md="3"><b-card><h6>Nivel</h6><h3>{{ loyalty.tier ? loyalty.tier.name : '—' }}</h3><small class="text-muted" v-if="loyalty.next_tier">faltan {{ loyalty.account.points_to_next_tier }} para {{ loyalty.next_tier.name }}</small></b-card></b-col>
              <b-col md="3"><b-card><h6>Acumulado</h6><h3>{{ loyalty.account.lifetime_points_earned }}</h3><small class="text-muted">ventana: {{ loyalty.account.tier_points_window }}</small></b-card></b-col>
              <b-col md="3"><b-card><h6>Por vencer</h6><div>30d: {{ loyalty.expiring_30 }}</div><div>60d: {{ loyalty.expiring_60 }}</div><div>90d: {{ loyalty.expiring_90 }}</div></b-card></b-col>
            </b-row>
            <b-row class="mt-3">
              <b-col>
                <b-button size="sm" variant="primary" @click="openAdjust">Ajustar puntos</b-button>
                <b-button size="sm" variant="outline-secondary" @click="recalcTier">Recalcular niveles</b-button>
              </b-col>
            </b-row>
            <b-row class="mt-3">
              <b-col>
                <h5>Movimientos</h5>
                <b-table small striped responsive :items="loyalty.ledger" :fields="ledgerFields" :busy="loyalty.loading">
                  <template #empty>Sin movimientos.</template>
                </b-table>
                <b-pagination v-model="loyalty.page" :total-rows="loyalty.total" :per-page="loyalty.limit" @change="onLedgerPage"></b-pagination>
              </b-col>
            </b-row>
          </div>
          <div v-else><p class="text-muted">Este cliente no tiene cuenta de lealtad.</p></div>
        </b-tab>
        <b-tab title="Beneficios" @click="loadBenefits">
          <b-row class="mb-2">
            <b-col>
              <b-form-select v-model="benefitDefToIssue" :options="benefitDefOptions" style="max-width: 320px; display:inline-block;"></b-form-select>
              <b-button size="sm" variant="primary" @click="issueBenefit" :disabled="!benefitDefToIssue">Emitir</b-button>
            </b-col>
          </b-row>
          <b-table small striped responsive :items="benefits.list" :fields="benefitFields" :busy="benefits.loading">
            <template #empty>Sin beneficios.</template>
            <template #cell(status)="data">
              <b-badge :variant="benefitVariant(data.item.status)">{{ data.item.status }}</b-badge>
            </template>
            <template #cell(buttons)="data">
              <b-button size="sm" variant="outline-danger" v-if="data.item.status==='issued' || data.item.status==='reserved'" @click="revokeBenefit(data.item)">Revocar</b-button>
              <b-button size="sm" variant="outline-secondary" v-if="data.item.status==='issued'" @click="extendBenefit(data.item)">Extender</b-button>
            </template>
          </b-table>
        </b-tab>
        <b-tab title="Referidos" @click="loadReferrals">
          <p class="text-muted">Código de referido del cliente: <code>{{ customer.referral_code }}</code></p>
          <b-table small striped responsive :items="referrals.list" :fields="referralFields" :busy="referrals.loading">
            <template #empty>Sin referidos.</template>
            <template #cell(status)="data"><b-badge :variant="refVariant(data.item.status)">{{ data.item.status }}</b-badge></template>
          </b-table>
        </b-tab>
        <b-tab title="360°" lazy>
          <customer360 v-if="customer.id" :customer-id="customer.id"></customer360>
        </b-tab>
        <b-tab title="Historial" disabled><p class="text-muted">Disponible en la Fase 2.</p></b-tab>
        <b-tab title="Encuestas/Casos" disabled><p class="text-muted">Disponible en la Fase 5.</p></b-tab>
      </b-tabs>

      <b-modal v-model="showAdjust" title="Ajustar puntos" @ok="saveAdjust" ok-title="Aplicar">
        <b-form-group label="Puntos (usa negativo para restar)">
          <b-form-input type="number" v-model.number="adjustForm.points"></b-form-input>
        </b-form-group>
        <b-form-group label="Motivo (obligatorio)">
          <b-form-input v-model="adjustForm.reason"></b-form-input>
        </b-form-group>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import customersService from "../services/customersService";
import loyaltyService from "../services/loyaltyService";
import benefitsService from "../services/benefitsService";
import marketingService from "../services/marketingService";
import Customer360 from "./executive/Customer360.vue";

export default {
  name: "cliente",
  components: { Nav, Customer360 },
  data() {
    return {
      loading: true,
      customer: {},
      preferredSucursal: null,
      statusOptions: [
        { value: "active", text: "Activo" },
        { value: "blocked", text: "Bloqueado" },
        { value: "deleted", text: "Eliminado" },
      ],
      identityFields: [
        { key: "provider", label: "Proveedor" },
        { key: "external_id", label: "ID externo" },
        { key: "created_at", label: "Vinculado" },
      ],
      loyalty: { account: null, tier: null, next_tier: null, expiring_30: 0, expiring_60: 0, expiring_90: 0, ledger: [], total: 0, page: 1, limit: 20, loading: false, loaded: false },
      ledgerFields: [
        { key: "created_at", label: "Fecha" },
        { key: "entry_type", label: "Tipo" },
        { key: "points", label: "Puntos" },
        { key: "points_remaining", label: "Restante" },
        { key: "reason", label: "Motivo" },
        { key: "ticket_id", label: "Ticket" },
      ],
      showAdjust: false,
      adjustForm: { points: 0, reason: "" },
      benefits: { list: [], loading: false, loaded: false },
      benefitFields: [
        { key: "code", label: "Código" },
        { key: "definition_name", label: "Beneficio" },
        { key: "effect_type", label: "Efecto" },
        { key: "status", label: "Estado" },
        { key: "expires_at", label: "Vence" },
        { key: "buttons", label: "" },
      ],
      benefitDefs: [],
      benefitDefToIssue: null,
      referrals: { list: [], loading: false },
      referralFields: [
        { key: "referred_name", label: "Referido" },
        { key: "code_used", label: "Código" },
        { key: "status", label: "Estado" },
        { key: "registered_at", label: "Registrado" },
      ],
    };
  },
  computed: {
    benefitDefOptions() {
      return [{ value: null, text: "— emitir beneficio —" }].concat(
        this.benefitDefs.map((d) => ({ value: d.id, text: d.code + " · " + d.name }))
      );
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      customersService
        .get(this.$route.params.id)
        .then((res) => {
          this.customer = res.data || {};
          this.preferredSucursal = this.customer.preferred_sucursal_id || null;
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => (this.loading = false));
    },
    save() {
      const payload = { ...this.customer, preferred_sucursal_id: this.preferredSucursal || null };
      customersService
        .update(this.customer.id, payload)
        .then((res) => {
          this.customer = res.data || this.customer;
          this.preferredSucursal = this.customer.preferred_sucursal_id || null;
          this.makeToast("Guardado", "Perfil actualizado", "success");
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    loadLoyalty() {
      if (this.loyalty.loaded) return;
      loyaltyService.getCustomerLoyalty(this.customer.id).then((res) => {
        const d = res.data || {};
        this.loyalty.account = d.account && d.account.customer_id ? d.account : null;
        this.loyalty.tier = d.tier || null;
        this.loyalty.next_tier = d.next_tier || null;
        this.loyalty.expiring_30 = d.expiring_30 || 0;
        this.loyalty.expiring_60 = d.expiring_60 || 0;
        this.loyalty.expiring_90 = d.expiring_90 || 0;
        this.loyalty.loaded = true;
        this.loadLedger();
      }).catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    loadLedger() {
      this.loyalty.loading = true;
      loyaltyService.getLedger(this.customer.id, { limit: this.loyalty.limit, offset: (this.loyalty.page - 1) * this.loyalty.limit })
        .then((res) => {
          this.loyalty.ledger = res.data.entries || [];
          this.loyalty.total = res.data.total || 0;
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => (this.loyalty.loading = false));
    },
    onLedgerPage(p) {
      this.loyalty.page = p;
      this.loadLedger();
    },
    openAdjust() {
      this.adjustForm = { points: 0, reason: "" };
      this.showAdjust = true;
    },
    saveAdjust(evt) {
      evt.preventDefault();
      if (!this.adjustForm.reason) {
        this.makeToast("Error", "El motivo es obligatorio", "danger");
        return;
      }
      loyaltyService.adjust({ customer_id: this.customer.id, points: this.adjustForm.points, reason: this.adjustForm.reason })
        .then((res) => {
          this.showAdjust = false;
          const d = res.data || {};
          if (d.account) this.loyalty.account = d.account;
          this.loadLedger();
          this.makeToast("Guardado", "Ajuste aplicado", "success");
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    recalcTier() {
      loyaltyService.runJob("loyalty_tier_review")
        .then(() => {
          this.loyalty.loaded = false;
          this.loadLoyalty();
          this.makeToast("Listo", "Revisión de niveles ejecutada", "success");
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    loadBenefits() {
      this.benefits.loading = true;
      benefitsService.customerBenefits(this.customer.id)
        .then((res) => { this.benefits.list = res.data || []; this.benefits.loaded = true; })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => (this.benefits.loading = false));
      if (!this.benefitDefs.length) {
        benefitsService.listDefinitions().then((res) => { this.benefitDefs = res.data || []; }).catch(() => {});
      }
    },
    issueBenefit() {
      benefitsService.issue({ customer_id: this.customer.id, definition_id: this.benefitDefToIssue })
        .then(() => { this.benefitDefToIssue = null; this.loadBenefits(); this.makeToast("Listo", "Beneficio emitido", "success"); })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    revokeBenefit(b) {
      benefitsService.revoke(b.id)
        .then(() => { this.loadBenefits(); this.makeToast("Listo", "Beneficio revocado", "success"); })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    extendBenefit(b) {
      const val = window.prompt("Nueva fecha de vencimiento (YYYY-MM-DD HH:MM:SS):", b.expires_at || "");
      if (!val) return;
      benefitsService.extend(b.id, val)
        .then(() => { this.loadBenefits(); this.makeToast("Listo", "Vigencia extendida", "success"); })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    benefitVariant(status) {
      return { issued: "success", reserved: "info", used: "secondary", expired: "warning", revoked: "danger" }[status] || "secondary";
    },
    loadReferrals() {
      this.referrals.loading = true;
      marketingService.customerReferrals(this.customer.id)
        .then((res) => { this.referrals.list = res.data || []; })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => (this.referrals.loading = false));
    },
    refVariant(s) {
      return { pending: "info", rewarded: "success", rejected: "danger" }[s] || "secondary";
    },
    errMsg(err) {
      if (err && err.response && err.response.data) {
        return typeof err.response.data === "string" ? err.response.data : (err.response.data.error || JSON.stringify(err.response.data));
      }
      return err && err.message ? err.message : "Error desconocido";
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, autoHideDelay: 5000, appendToast: true });
    },
  },
};
</script>
